import { Inject, Injectable, PLATFORM_ID, computed, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const STORAGE_KEY = 'orderyaa.textsize.v1';

/**
 * Shopper-controlled text size.
 *
 * IMPLEMENTED BY SCALING THE ROOT FONT SIZE, not by overriding individual rules. This app sizes almost
 * everything in `rem`, so one `font-size` on <html> scales type, padding and pill heights together and
 * the layout stays proportional. A per-element approach would need touching every stylesheet and would
 * still leave buttons the wrong size for their label.
 *
 * WHY THIS EXISTS ALONGSIDE BROWSER ZOOM: the audience for a "bigger text" button — an older shopper on
 * a phone, someone reading Urdu Naskh at a glance — is exactly the audience least likely to know where
 * browser zoom lives. It is one visible tap in the header.
 *
 * The steps are multiplicative on the browser's OWN base size, so a shopper who has already raised their
 * device font keeps that as the starting point rather than having it overwritten.
 */
@Injectable({ providedIn: 'root' })
export class TextSizeService {
  private readonly isBrowser: boolean;

  /**
   * Percentages of the browser default. 100 is untouched; the ceiling stops at 150 because past that
   * this layout's fixed-width elements (the header pill, the cart badge) start colliding.
   */
  private static readonly STEPS = [100, 112, 125, 137, 150];
  private static readonly DEFAULT_INDEX = 0;

  private readonly index = signal(TextSizeService.DEFAULT_INDEX);

  /** Current size as a percentage of the browser default — shown on the control. */
  readonly percent = computed(() => TextSizeService.STEPS[this.index()]);
  readonly canIncrease = computed(() => this.index() < TextSizeService.STEPS.length - 1);
  readonly canDecrease = computed(() => this.index() > 0);
  /** True when the shopper has changed it from the default — lets the UI offer a reset. */
  readonly isCustom = computed(() => this.index() !== TextSizeService.DEFAULT_INDEX);

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) return;
    const saved = this.read();
    if (saved !== null) this.index.set(saved);
    this.apply();
  }

  increase(): void { this.step(1); }
  decrease(): void { this.step(-1); }
  reset(): void { this.index.set(TextSizeService.DEFAULT_INDEX); this.persist(); this.apply(); }

  private step(delta: number): void {
    const next = this.index() + delta;
    if (next < 0 || next >= TextSizeService.STEPS.length) return;
    this.index.set(next);
    this.persist();
    this.apply();
  }

  /**
   * Write the scale onto <html>. At 100% the inline style is REMOVED rather than set to "100%", so a
   * shopper who never touched this is left with whatever their browser/OS decided — including a larger
   * accessibility default we must not clobber.
   */
  private apply(): void {
    if (!this.isBrowser) return;
    try {
      const el = document.documentElement;
      if (this.index() === TextSizeService.DEFAULT_INDEX) el.style.removeProperty('font-size');
      else el.style.fontSize = `${this.percent()}%`;
    } catch {
      /* locked-down document — the app still works at the default size */
    }
  }

  private read(): number | null {
    try {
      const n = Number(localStorage.getItem(STORAGE_KEY));
      return Number.isInteger(n) && n >= 0 && n < TextSizeService.STEPS.length ? n : null;
    } catch {
      return null;
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(this.index()));
    } catch {
      /* private mode — the choice just won't survive a reload */
    }
  }
}
